import React, { useEffect, useMemo, useState } from 'react';
import { CheckCheck, Download, RotateCcw, Send, Undo2 } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { Chip } from '../attendance/shared';
import {
  DayHead, downloadCsv, ErrorBox, fmt, Loading, matches, n0, PeriodPick, PyHeader, SearchBox, Sel, SRC_LABEL, Stat, usePayrollMonth,
} from './shared';

// Attendance Management > Site Sheets, Attendance Check and Attendance Grid (Stephanet's pay15/Sheets: PySiteSheets, PyAttCheck,
// PyAttGrid). The sheets fill themselves from the line that counts each day (manual over leave over device over imported).
// Each site's sheet for the month is approved per staff type — monthly staff and daily-rate staff — through the SITE_SHEET
// approval: raised by the HR department, then the HR department head, then DMD / MD. An approved sheet locks its lines.

const SHEET_TONE = { draft: 'gray', pending_head: 'yellow', pending_approval: 'purple', returned: 'red', approved: 'green' };
const STAFF = [['basic', 'Monthly staff'], ['daily', 'Daily-rate staff']];

function useMonth(fetcher) {
  const [cal, period, setPeriod] = usePayrollMonth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!period) return;
    setD(null);
    fetcher(period).then(x => { setD(x); setErr(''); }).catch(e => setErr(e.message));
  }, [period, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return { cal, period, setPeriod, d, err, reload: () => setTick(t => t + 1) };
}

// ── Site Sheets ──
export function SiteSheets() {
  const { notify } = useHR();
  const { cal, period, setPeriod, d, err, reload } = useMonth(hr.getSiteSheets);
  const [site, setSite] = useState('');
  const [staff, setStaff] = useState('basic');
  const [q, setQ] = useState('');
  const [act, setAct] = useState(null);   // {action, label} needing a comment
  const sites = useMemo(() => {
    if (!d) return [];
    const by = {};
    d.sheets.forEach(s => { by[s.site_id] = by[s.site_id] || { id: s.site_id, name: s.site, rows: 0 }; by[s.site_id].rows += s.rows.length; });
    return Object.values(by).sort((a, b) => b.rows - a.rows || (a.name || '').localeCompare(b.name || ''));
  }, [d]);
  useEffect(() => { if (d && (!site || !sites.some(s => String(s.id) === site))) setSite(sites[0] ? String(sites[0].id) : ''); }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const sheet = d?.sheets.find(s => String(s.site_id) === site && s.staff_type === staff);
  const rows = useMemo(() => (sheet ? sheet.rows.filter(r => matches(r, q, ['employee_no', 'name'])) : []), [sheet, q]);
  if (err) return <ErrorBox error={err} />;
  if (!d || !cal) return <Loading what="the site sheets" />;
  const days = d.period.days;
  const T = k => rows.reduce((t, r) => t + (r[k] || 0), 0);
  const status = sheet?.status || 'draft';
  const siteName = sites.find(s => String(s.id) === site)?.name;
  const run = async (action, comment) => {
    try {
      const r = await hr.siteSheetAction(period, { site_id: site, staff_type: staff, action, comment });
      notify(`${r.reference}: ${r.status_label}`); setAct(null); reload();
    } catch (e) { notify(e.message, 'bad'); }
  };
  const exp = () => downloadCsv(`site-sheet-${(siteName || 'site').replace(/\W+/g, '-')}-${staff}-${period}.csv`,
    ['Employee ID', 'Name', 'Department', 'Days worked', 'Working-day hrs', 'Rest-day hrs', 'Holiday hrs', 'Overtime hrs', 'TP days', 'Days present', 'Total hrs',
      ...days.flatMap(x => [`${x.date} HRS`, `${x.date} TP`, `${x.date} OT`])],
    rows.map(r => [r.employee_no, r.name, r.department, fmt(r.days), fmt(r.wd_hours), fmt(r.rd_hours), fmt(r.ph_hours), fmt(r.ot_hours), r.tp, r.present, fmt(r.total_hours),
      ...days.flatMap(x => { const c = r.cells[x.date]; return c ? [fmt(c.h), c.tp ? 1 : '', fmt(c.ot)] : ['', '', '']; })]));
  return (
    <div className="ps py">
      <PyHeader title={`Attendance — ${siteName || '(choose the project)'}`} cal={cal} period={period}
                sub="Fills by itself from Mark Daily Attendance, Attendance Import, Attendance Manual, leave and the devices. HRS = hours (8 = full day) · TP = site transport · OT = overtime. Blue = rest day, pink = public holiday.">
        <button className="mdr-btn" onClick={exp} disabled={!sheet}><Download size={14} /> Export</button>
      </PyHeader>
      <div className="mdr-card ps-filters no-print">
        <PeriodPick cal={cal} value={period} onChange={setPeriod} />
        <Sel label="Project / site sheet" value={site} onChange={setSite} all={null} options={sites.map(s => ({ value: String(s.id), label: `${s.name}${s.rows ? ` (${s.rows})` : ''}` }))} />
        <Sel label="Staff" value={staff} onChange={setStaff} all={null} options={STAFF.map(([value, label]) => ({ value, label }))} />
        <SearchBox value={q} onChange={setQ} />
        <div className="ps-runbar inline">Period {d.period.text} · Hrs/day {d.hours_per_day}</div>
      </div>
      {!d.hr_department_set && <div className="py-banner">The HR department is not set — choose it in Time & Attendance › T&A Settings so site sheets can be submitted.</div>}
      {site && <div className="mdr-card py-approval">
        <div><b>{STAFF.find(s => s[0] === staff)[1]} sheet</b> {sheet?.reference && <b className="mdr-id">{sheet.reference}</b>}{' '}
          <Chip tone={SHEET_TONE[status]}>{sheet?.status_label || 'Draft'}</Chip>
          {sheet?.with && <span className="muted small"> · with {sheet.with}</span>}
          {sheet?.current_step && !sheet?.with && <span className="muted small"> · at {sheet.current_step}</span>}
          {sheet?.submitted_by && <span className="muted small"> · raised by {sheet.submitted_by}</span>}</div>
        <div className="ps-top-actions no-print">
          {d.can.submit && ['draft', 'returned'].includes(status) && sheet?.rows.length > 0 &&
            <button className="mds-run" onClick={() => run('submit')}><Send size={14} /> {status === 'returned' ? 'Resubmit' : 'Submit for Approval'}</button>}
          {['pending_head', 'pending_approval'].includes(status) && <>
            <button className="mds-run" onClick={() => run('approve')}><CheckCheck size={14} /> Approve</button>
            <button className="mdr-btn" onClick={() => setAct({ action: 'return', label: 'Return the site sheet' })}><Undo2 size={14} /> Return</button></>}
          {d.can.reopen && status === 'approved' && <button className="mdr-btn" onClick={() => setAct({ action: 'reopen', label: 'Reopen the site sheet' })}><RotateCcw size={14} /> Reopen</button>}
        </div>
        {(sheet?.history?.length > 0 || sheet?.reopened?.length > 0) && <div className="py-history">
          {(sheet.reopened || []).map((h, i) => <span key={`r${i}`}><b>Reopened</b> by {h.by || '—'}{h.at ? ` on ${h.at.slice(0, 10)}` : ''} — “{h.reason}”</span>)}
          {(sheet.history || []).map((h, i) => <span key={i}><b>{h.step}</b> {h.status} by {h.by || '—'}{h.comment ? ` — “${h.comment}”` : ''}</span>)}</div>}
        {status === 'approved' && <p className="muted small">Approved — its attendance lines are locked until HR reopens the sheet.</p>}
      </div>}
      <div className="py-stats">
        <Stat label="Staff on this sheet" value={rows.filter(r => r.total_hours > 0).length} /><Stat label="Days worked" value={n0(T('days'))} tone="blue" />
        <Stat label="Working-day hrs" value={n0(T('wd_hours'))} /><Stat label="Rest-day hrs" value={n0(T('rd_hours'))} /><Stat label="Holiday hrs" value={n0(T('ph_hours'))} tone="pink" />
        <Stat label="Overtime hrs" value={n0(T('ot_hours'))} tone="amber" /><Stat label="TP days" value={n0(T('tp'))} /><Stat label="Total hrs" value={n0(T('total_hours'))} tone="green" />
      </div>
      <div className="ps-wb" style={{ maxHeight: 620 }}>
        <table className="ps-table py-sheet">
          <thead>
            <tr><th className="fz" style={{ left: 0, minWidth: 90 }} rowSpan={2}>Employee ID</th><th className="fz" style={{ left: 90, minWidth: 160 }} rowSpan={2}>Name</th>
              {['Days worked', 'Working-day hrs', 'Rest-day hrs', 'Holiday hrs', 'Overtime hrs', 'TP days', 'Days present', 'Total hrs'].map(h => <th key={h} rowSpan={2} className="r">{h}</th>)}
              <DayHead days={days} span={3} /></tr>
            <tr>{days.map(x => ['HRS', 'TP', 'OT'].map(k => <th key={x.date + k} className={`c py-sub t-${x.type}`}>{k}</th>))}</tr>
          </thead>
          <tbody>{rows.map(r => (
            <tr key={r.employee_id}>
              <td className="fz" style={{ left: 0, minWidth: 90 }}>{r.employee_no}</td>
              <td className="fz" style={{ left: 90, minWidth: 160 }}>{r.name}{r.multi_site_days > 0 && <small className="py-bad" title="Days with hours on another site too — see Attendance Check"> · {r.multi_site_days} on 2 sites</small>}</td>
              {['days', 'wd_hours', 'rd_hours', 'ph_hours', 'ot_hours', 'tp', 'present', 'total_hours'].map(k => <td key={k} className="r mono">{fmt(r[k])}</td>)}
              {days.map(x => {
                const c = r.cells[x.date];
                const cls = `c t-${x.type} ${c?.src === 'manual' ? 'py-man' : ''} ${c?.conflict ? 'py-cnt-bad' : ''}`;
                const title = c ? `${SRC_LABEL[c.src] || c.src}${c.conflict ? ' — lines disagree' : ''}` : '';
                return [<td key={`${x.date}h`} className={cls} title={title}>{c ? fmt(c.h) : ''}</td>,
                  <td key={`${x.date}t`} className={`c t-${x.type}`}>{c?.tp ? 1 : ''}</td>, <td key={`${x.date}o`} className={`c t-${x.type}`}>{c ? fmt(c.ot) : ''}</td>];
              })}
            </tr>))}
          {!rows.length && <tr><td colSpan={10 + days.length * 3} className="ps-empty">No hours on this site sheet for the month.</td></tr>}</tbody>
        </table>
      </div>
      <p className="muted small py-foot">Underlined hours come from a typed line (Mark Daily Attendance / Attendance Manual) — they count over the device line. Red cells: the day's typed, leave and device lines disagree. Make corrections in Attendance Manual.</p>
      {act && <CommentModal title={act.label} onClose={() => setAct(null)} onSave={c => run(act.action, c)} />}
    </div>
  );
}

function CommentModal({ title, onClose, onSave }) {
  const [c, setC] = useState('');
  return (
    <Modal title={title} onClose={onClose}>
      <label className="rec-f s2"><span>Reason *</span><textarea rows={3} value={c} onChange={e => setC(e.target.value)} /></label>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className="mds-run" disabled={!c.trim()} onClick={() => onSave(c.trim())}>Confirm</button></div>
    </Modal>
  );
}

// ── Attendance Check ──
export function AttendanceCheck() {
  const { cal, period, setPeriod, d, err } = useMonth(hr.getAttendanceGrid);
  const [chk, setChk] = useState(null);
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('any');
  useEffect(() => { if (period) hr.getAttendanceCheck(period).then(setChk).catch(() => setChk(null)); }, [period]);
  const rows = useMemo(() => (d ? d.employees.map(e => {
    const counts = Object.fromEntries(Object.entries(e.cells).map(([k, v]) => [k, new Set(v.filter(c => c.h > 0).map(c => c.site)).size]));
    return { ...e, counts, multiN: Object.values(counts).filter(n => n > 1).length, anyN: Object.values(counts).filter(n => n > 0).length };
  }).filter(e => (only === 'all' || (only === 'multi' ? e.multiN : e.anyN)) && matches(e, q, ['employee_no', 'name'])) : []), [d, q, only]);
  if (err) return <ErrorBox error={err} />;
  if (!d || !cal) return <Loading what="the attendance check" />;
  const days = d.period.days;
  const bad = rows.filter(r => r.multiN).length;
  const conflicts = (chk?.rows || []).filter(r => r.conflict);
  return (
    <div className="ps py">
      <PyHeader title="Attendance Check" cal={cal} period={period}
                sub="Each cell = number of sites with hours for that person on that day. Anything above 1 is shown red and would be paid twice — fix it in Attendance Manual." />
      <div className="mdr-card ps-filters no-print">
        <PeriodPick cal={cal} value={period} onChange={setPeriod} /><SearchBox value={q} onChange={setQ} />
        <Sel label="Show" value={only} onChange={setOnly} all={null} options={[{ value: 'any', label: 'Staff with any hours' }, { value: 'multi', label: 'Only staff on 2+ sites' }, { value: 'all', label: 'Everyone' }]} />
        <div className={`ps-runbar inline ${bad ? 'py-bad' : ''}`}>Staff with the same day on 2+ sites: <b>{bad}</b> · days whose lines disagree: <b>{conflicts.length}</b></div>
      </div>
      {conflicts.length > 0 && <div className="mdr-card py-conflicts">
        <b>Days whose typed, leave and device lines disagree</b>
        {conflicts.map(r => (
          <div key={`${r.employee_id}${r.date}`} className="py-conflict"><span>{r.name} ({r.employee_no}) · {r.weekday} {r.date}</span>
            <span>{r.lines.map(l => `${l.source_label}: ${l.site || '—'} ${l.hours}h${l.counts ? ' ✓ counts' : ''}`).join(' · ')}</span></div>))}
      </div>}
      <div className="ps-wb" style={{ maxHeight: 620 }}>
        <table className="ps-table py-sheet">
          <thead><tr><th className="fz" style={{ left: 0, minWidth: 90 }}>Employee ID</th><th className="fz" style={{ left: 90, minWidth: 160 }}>Name</th>
            <th className="r">Days on 2+ sites</th><th className="r">Days with any hours</th><DayHead days={days} /></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}><td className="fz" style={{ left: 0, minWidth: 90 }}>{r.employee_no}</td><td className="fz" style={{ left: 90, minWidth: 160 }}>{r.name}</td>
              <td className={`r mono ${r.multiN ? 'py-bad' : ''}`}>{r.multiN}</td><td className="r mono">{r.anyN}</td>
              {days.map(x => { const n = r.counts[x.date] || 0; return <td key={x.date} className={`c t-${x.type} ${n > 1 ? 'py-cnt-bad' : ''}`}>{n}</td>; })}</tr>))}
          {!rows.length && <tr><td colSpan={4 + days.length} className="ps-empty">Nothing to show.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Attendance Grid ──
export function AttendanceGrid() {
  const { cal, period, setPeriod, d, err } = useMonth(hr.getAttendanceGrid);
  const [q, setQ] = useState('');
  const [site, setSite] = useState('');
  const siteNames = useMemo(() => (d ? [...new Set(d.employees.flatMap(e => Object.values(e.cells).flatMap(v => v.map(c => c.site))).filter(Boolean))].sort() : []), [d]);
  const rows = useMemo(() => (d ? d.employees.filter(e => Object.keys(e.cells).length && (!site || Object.values(e.cells).some(v => v.some(c => c.site === site)))
    && matches(e, q, ['employee_no', 'name'])) : []), [d, q, site]);
  if (err) return <ErrorBox error={err} />;
  if (!d || !cal) return <Loading what="the attendance grid" />;
  const days = d.period.days;
  const short = s => (s || '').split(' ').map(w => w[0]).join('').slice(0, 4).toUpperCase();
  return (
    <div className="ps py">
      <PyHeader title="Attendance Grid" cal={cal} period={period}
                sub="Where each person was on each day: every line — typed, leave, device or imported — with its site, hours, overtime and TP. The line that counts is bold; the others are dimmed." />
      <div className="mdr-card ps-filters no-print">
        <PeriodPick cal={cal} value={period} onChange={setPeriod} /><SearchBox value={q} onChange={setQ} />
        <Sel label="Site" value={site} onChange={setSite} all="All sites" options={siteNames} />
      </div>
      <div className="ps-wb" style={{ maxHeight: 620 }}>
        <table className="ps-table py-sheet py-grid">
          <thead><tr><th className="fz" style={{ left: 0, minWidth: 90 }}>Employee ID</th><th className="fz" style={{ left: 90, minWidth: 160 }}>Name</th><DayHead days={days} /></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}><td className="fz" style={{ left: 0, minWidth: 90 }}>{r.employee_no}</td><td className="fz" style={{ left: 90, minWidth: 160 }}>{r.name}</td>
              {days.map(x => {
                const v = r.cells[x.date] || [];
                return (
                  <td key={x.date} className={`c t-${x.type}`}
                      title={v.map(c => `${c.site || '—'} · ${c.h}h${c.ot ? ` + ${c.ot} OT` : ''}${c.tp ? ' · TP' : ''} · ${SRC_LABEL[c.src] || c.src}${c.counts ? ' (counts)' : ''}`).join('\n')}>
                    {v.map((c, i) => <div key={i} className={`py-gc ${c.src === 'manual' ? 'man' : ''} ${c.h ? '' : 'zero'}`} style={c.counts ? undefined : { opacity: 0.45 }}>
                      <b>{short(c.site)}</b>{fmt(c.h)}{c.ot ? <i>+{fmt(c.ot)}</i> : null}</div>)}
                  </td>
                );
              })}</tr>))}
          {!rows.length && <tr><td colSpan={2 + days.length} className="ps-empty">No attendance in this month.</td></tr>}</tbody>
        </table>
      </div>
      <div className="py-legend">{siteNames.filter(s => !site || s === site).map(s => <span key={s}><b>{short(s)}</b> {s}</span>)}</div>
    </div>
  );
}

export default SiteSheets;
